import React, { useEffect, useMemo, useState } from 'react';
import {
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import {
  AssignmentTurnedIn,
  Build,
  FactCheck,
  Feedback as FeedbackIcon,
  FlightTakeoff,
  Handyman,
  Inventory2,
  MonetizationOn,
  MoveToInbox,
  Refresh,
  ArrowForward,
  MoreVert,
  Assessment,
  Gavel,
  Timeline
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip as RechartsTooltip,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid
} from 'recharts';

const THEME = {
  ink: '#0b1f33',
  navy: '#132a43',
  steel: '#3d5a80',
  mist: '#e8eef5',
  paper: '#f4f7fb',
  card: '#ffffff',
  border: '#d5dee9',
  gold: '#9a7b4f',
  goldSoft: '#c4a574',
  success: '#1b6b4a',
  warning: '#a16207',
  info: '#1d4f7c',
  muted: '#5c6b7a'
};

const EMPTY_COUNTS = {
  charterAmApprovals: 0,
  charterCamoApprovals: 0,
  maintenancePlanning: 0,
  maintenanceMroInProgress: 0,
  poAmApprovals: 0,
  planningIntentAmApprovals: 0,
  loanAmApprovals: 0,
  receivingPendingInspectionStore: 0,
  receivingPendingInspectionLoan: 0,
  changeRequestAmApprovals: 0
};

/** Independent planning intents: reorder rows from Planning (PLANNING_NON_WO) awaiting AM approval — matches Planning.js (status PENDING). */
const isPlanningIntentPendingAm = (req) =>
  Boolean(
    req &&
      req.createdFrom === 'PLANNING_NON_WO' &&
      req.status === 'PENDING' &&
      req.isArchived !== true
  );

const formatUpdatedAt = (date) => {
  if (!date) return 'Never';
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  }).format(date);
};

const ChartTooltipContent = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const item = payload[0];
  return (
    <Paper
      elevation={0}
      sx={{
        px: 1.5,
        py: 1,
        border: `1px solid ${THEME.border}`,
        bgcolor: THEME.card,
        borderRadius: 1.5,
        boxShadow: `0 8px 24px ${alpha(THEME.ink, 0.12)}`
      }}
    >
      <Typography variant="caption" sx={{ color: THEME.muted, display: 'block' }}>
        {item.name || item.payload?.name}
      </Typography>
      <Typography variant="subtitle2" sx={{ color: THEME.ink, fontWeight: 700 }}>
        {item.value}
      </Typography>
    </Paper>
  );
};

const SummaryMetric = ({ label, value, hint, tone = 'navy', loading }) => {
  const toneColor =
    tone === 'gold' ? THEME.gold : tone === 'success' ? THEME.success : tone === 'warning' ? THEME.warning : THEME.navy;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.25,
        height: '100%',
        borderRadius: 2.5,
        border: `1px solid ${THEME.border}`,
        background: `linear-gradient(145deg, ${THEME.card} 0%, ${alpha(toneColor, 0.06)} 100%)`,
        position: 'relative',
        overflow: 'hidden',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 10px 28px ${alpha(THEME.ink, 0.08)}`
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          bgcolor: toneColor
        }
      }}
    >
      <Typography
        variant="overline"
        sx={{ color: THEME.muted, letterSpacing: 1.2, fontWeight: 600, lineHeight: 1.2 }}
      >
        {label}
      </Typography>
      <Typography
        variant="h3"
        sx={{
          mt: 0.75,
          mb: 0.5,
          color: THEME.ink,
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
          letterSpacing: '-0.02em'
        }}
      >
        {loading ? '—' : value}
      </Typography>
      <Typography variant="body2" sx={{ color: THEME.muted }}>
        {hint}
      </Typography>
    </Paper>
  );
};

const WorkloadCard = ({ card, loading, onOpen, onOpenTasks }) => {
  const accent = card.accent || THEME.steel;
  const hasItems = !loading && card.value > 0;

  return (
    <Paper
      elevation={0}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2.5,
        border: `1px solid ${hasItems ? alpha(accent, 0.35) : THEME.border}`,
        bgcolor: THEME.card,
        overflow: 'hidden',
        transition: 'border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
        '&:hover': {
          borderColor: alpha(accent, 0.55),
          boxShadow: `0 12px 32px ${alpha(THEME.ink, 0.07)}`,
          transform: 'translateY(-2px)'
        }
      }}
    >
      <Box
        sx={{
          px: 2,
          pt: 2,
          pb: 1.5,
          background: `linear-gradient(180deg, ${alpha(accent, 0.08)} 0%, transparent 100%)`
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1.5,
              display: 'grid',
              placeItems: 'center',
              bgcolor: alpha(accent, 0.12),
              color: accent,
              flexShrink: 0
            }}
          >
            {card.icon}
          </Box>
          <Chip
            size="small"
            label={card.category}
            sx={{
              height: 22,
              fontSize: '0.68rem',
              fontWeight: 600,
              bgcolor: alpha(accent, 0.1),
              color: accent,
              border: `1px solid ${alpha(accent, 0.2)}`
            }}
          />
        </Stack>
        <Typography
          variant="subtitle1"
          sx={{ mt: 1.5, color: THEME.ink, fontWeight: 700, lineHeight: 1.3 }}
        >
          {card.title}
        </Typography>
      </Box>

      <Box sx={{ px: 2, pb: 2, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Typography
          variant="h3"
          sx={{
            color: hasItems ? accent : THEME.muted,
            fontWeight: 700,
            fontVariantNumeric: 'tabular-nums',
            letterSpacing: '-0.02em',
            mb: 0.75
          }}
        >
          {loading ? <CircularProgress size={22} sx={{ color: THEME.steel }} /> : card.value}
        </Typography>
        <Typography variant="body2" sx={{ color: THEME.muted, flexGrow: 1, mb: 2 }}>
          {card.description}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="contained"
            endIcon={<ArrowForward sx={{ fontSize: 16 }} />}
            onClick={onOpen}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: THEME.navy,
              boxShadow: 'none',
              '&:hover': { bgcolor: THEME.ink, boxShadow: 'none' }
            }}
          >
            {card.actionLabel}
          </Button>
          <Tooltip title="Focused task shortcuts">
            <IconButton
              size="small"
              onClick={onOpenTasks}
              sx={{
                border: `1px solid ${THEME.border}`,
                borderRadius: 1.25,
                color: THEME.steel
              }}
            >
              <MoreVert fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
    </Paper>
  );
};

const AccountableManagerDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [counts, setCounts] = useState(EMPTY_COUNTS);
  const [taskMenuAnchor, setTaskMenuAnchor] = useState(null);
  const [taskMenuKey, setTaskMenuKey] = useState(null);

  const pendingAmDecisions = useMemo(
    () =>
      (counts.charterAmApprovals || 0) +
      (counts.poAmApprovals || 0) +
      (counts.planningIntentAmApprovals || 0) +
      (counts.loanAmApprovals || 0) +
      (counts.receivingPendingInspectionStore || 0) +
      (counts.receivingPendingInspectionLoan || 0) +
      (counts.changeRequestAmApprovals || 0),
    [counts]
  );

  const followUpWorkload = useMemo(
    () =>
      (counts.charterCamoApprovals || 0) +
      (counts.maintenancePlanning || 0) +
      (counts.maintenanceMroInProgress || 0),
    [counts]
  );

  const totalOpenItems = pendingAmDecisions + followUpWorkload;

  const compositionData = useMemo(
    () =>
      [
        { name: 'Pending AM decisions', value: pendingAmDecisions, color: THEME.gold },
        { name: 'Follow-up workload', value: followUpWorkload, color: THEME.steel }
      ].filter((d) => d.value > 0),
    [pendingAmDecisions, followUpWorkload]
  );

  const queueBarData = useMemo(
    () =>
      [
        { name: 'Charter AM', value: counts.charterAmApprovals, fill: THEME.gold },
        { name: 'PO AM', value: counts.poAmApprovals, fill: THEME.warning },
        { name: 'Planning Intent', value: counts.planningIntentAmApprovals, fill: '#8b6914' },
        { name: 'Loan-Out', value: counts.loanAmApprovals, fill: '#6b5b3e' },
        { name: 'Store Receiving', value: counts.receivingPendingInspectionStore, fill: THEME.info },
        { name: 'Loan-In Receiving', value: counts.receivingPendingInspectionLoan, fill: '#2a6f97' },
        { name: 'Change Requests', value: counts.changeRequestAmApprovals, fill: '#7a5c45' },
        { name: 'CAMO Approval', value: counts.charterCamoApprovals, fill: THEME.steel },
        { name: 'Planning Queue', value: counts.maintenancePlanning, fill: '#4a6fa5' },
        { name: 'MRO In Progress', value: counts.maintenanceMroInProgress, fill: THEME.success }
      ].filter((d) => d.value > 0),
    [counts]
  );

  const fetchData = async () => {
    setLoading(true);
    try {
      const [
        tripsResult,
        maintenanceResult,
        poResult,
        reorderResult,
        loanResult,
        receivingStoreResult,
        changeRequestResult
      ] = await Promise.allSettled([
        axios.get('/api/sales/trips'),
        axios.get('/api/maintenance'),
        axios.get('/api/store/purchase-orders', { params: { status: 'PENDING_AM_APPROVAL' } }),
        axios.get('/api/store/reorder-requests'),
        axios.get('/api/store/loan/issuances', {
          params: { loanDirection: 'LOAN_OUT', approvalStatus: 'PENDING_AM_APPROVAL' }
        }),
        axios.get('/api/store/receiving', { params: { workflowStatus: 'PENDING_INSPECTOR_APPROVAL' } }),
        axios.get('/api/v1/feedback', {
          params: {
            type: 'Change Request Form',
            amApprovalStatus: 'PENDING',
            limit: 100
          }
        })
      ]);

      const trips = tripsResult.status === 'fulfilled' ? (tripsResult.value.data || []) : [];
      const maintenance = maintenanceResult.status === 'fulfilled' ? (maintenanceResult.value.data || []) : [];
      const purchaseOrders = poResult.status === 'fulfilled' ? (poResult.value.data || []) : [];
      const reorderRequests = reorderResult.status === 'fulfilled' ? (reorderResult.value.data || []) : [];
      const loanApprovals = loanResult.status === 'fulfilled' ? (loanResult.value.data || []) : [];
      const receivingStorePending =
        receivingStoreResult.status === 'fulfilled' ? (receivingStoreResult.value.data || []) : [];

      const receivingPendingInspectionLoan = receivingStorePending.filter(
        (r) => r && String(r.loanDirection || '') === 'LOAN_IN'
      ).length;
      const receivingPendingInspectionStore = receivingStorePending.filter(
        (r) => !r || String(r.loanDirection || '') !== 'LOAN_IN'
      ).length;

      const charterAmApprovals = trips.filter(
        (trip) =>
          trip?.status === 'CREW_ASSIGNMENT' &&
          trip?.approvals?.accountableManager?.approved !== true
      ).length;

      const charterCamoApprovals = trips.filter(
        (trip) =>
          trip?.status === 'CREW_ASSIGNMENT' &&
          trip?.approvals?.accountableManager?.approved === true &&
          trip?.approvals?.camo?.approved !== true
      ).length;

      const maintenancePlanning = maintenance.filter(
        (wo) =>
          wo?.status === 'SCHEDULED' &&
          wo?.camoQmAcknowledged === true &&
          wo?.planningApproved !== true &&
          !wo?.planningRejectedAt
      ).length;

      const maintenanceMroInProgress = maintenance.filter((wo) => wo?.status === 'IN_PROGRESS').length;
      const poAmApprovals = Array.isArray(purchaseOrders) ? purchaseOrders.length : 0;

      const planningIntentAmApprovals = reorderRequests.filter(isPlanningIntentPendingAm).length;

      const loanAmApprovals = Array.isArray(loanApprovals) ? loanApprovals.length : 0;

      const changeRequestFeedback =
        changeRequestResult.status === 'fulfilled'
          ? changeRequestResult.value.data?.feedback || []
          : [];
      const changeRequestAmApprovals = Array.isArray(changeRequestFeedback)
        ? changeRequestFeedback.length
        : 0;

      setCounts({
        charterAmApprovals,
        charterCamoApprovals,
        maintenancePlanning,
        maintenanceMroInProgress,
        poAmApprovals,
        planningIntentAmApprovals,
        loanAmApprovals,
        receivingPendingInspectionStore,
        receivingPendingInspectionLoan,
        changeRequestAmApprovals
      });
      setLastUpdated(new Date());
    } catch (error) {
      setCounts(EMPTY_COUNTS);
      setLastUpdated(new Date());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const taskActionsByCard = useMemo(
    () => ({
      charterAmApprovals: [
        {
          label: 'Review charters pending AM approval',
          action: () => navigate({ pathname: '/sales', search: '?amFocus=charter_am' })
        }
      ],
      charterCamoApprovals: [
        {
          label: 'Review and approve charters pending CAMO',
          action: () => navigate({ pathname: '/sales', search: '?amFocus=charter_camo' })
        }
      ],
      maintenancePlanning: [
        {
          label: 'Open planning task queue',
          action: () => navigate({ pathname: '/planning', search: '?tab=tasks' })
        },
        {
          label: 'Open planning dashboard',
          action: () => navigate({ pathname: '/planning', search: '?tab=dashboard' })
        }
      ],
      poAmApprovals: [
        {
          label: 'Review POs pending AM approval',
          action: () => navigate({ pathname: '/procurement', search: '?poStatus=PENDING_AM_APPROVAL' })
        }
      ],
      planningIntentAmApprovals: [
        {
          label: 'AM approve planning intents',
          action: () => navigate({ pathname: '/planning', search: '?tab=procurement' })
        }
      ],
      loanAmApprovals: [
        {
          label: 'Open loan-out AM approvals',
          action: () => navigate('/store', { state: { tab: 10, loanTab: 3 } })
        }
      ],
      maintenanceMroInProgress: [
        {
          label: 'Open maintenance board',
          action: () => navigate('/maintenance')
        }
      ],
      receivingPendingInspectionStore: [
        {
          label: 'Open store receiving — pending inspection',
          action: () =>
            navigate('/store/receiving', {
              state: { receivingInventoryDomain: 'STORE', receivingWorkflowFilter: 'PENDING' }
            })
        }
      ],
      receivingPendingInspectionLoan: [
        {
          label: 'Open store receiving — loan-in pending',
          action: () =>
            navigate('/store/receiving', {
              state: { receivingInventoryDomain: 'STORE', receivingWorkflowFilter: 'PENDING' }
            })
        }
      ],
      changeRequestAmApprovals: [
        {
          label: 'Review change requests pending AM approval',
          action: () =>
            navigate('/feedback', {
              state: {
                feedbackFilters: {
                  type: 'Change Request Form',
                  amApprovalStatus: 'PENDING'
                }
              }
            })
        }
      ]
    }),
    [navigate]
  );

  const navigateCardPrimary = (card) => {
    if (card.navigateState) {
      navigate(card.path, { state: card.navigateState });
      return;
    }
    navigate(card.path);
  };

  const cards = [
    {
      key: 'charterAmApprovals',
      title: 'Charter AM Approvals',
      value: counts.charterAmApprovals,
      icon: <FlightTakeoff fontSize="small" />,
      description: 'Trips waiting for AM decision before CAMO approval.',
      actionLabel: 'Open Sales',
      path: '/sales',
      category: 'Decision',
      accent: THEME.gold
    },
    {
      key: 'charterCamoApprovals',
      title: 'Charter CAMO Approval',
      value: counts.charterCamoApprovals,
      icon: <AssignmentTurnedIn fontSize="small" />,
      description: 'AM already approved; CAMO airworthiness still pending — AM can approve or follow up with CAMO.',
      actionLabel: 'Open Sales',
      path: '/sales',
      category: 'Follow-up',
      accent: THEME.steel
    },
    {
      key: 'maintenancePlanning',
      title: 'Maintenance Planning Queue',
      value: counts.maintenancePlanning,
      icon: <Build fontSize="small" />,
      description: 'Acknowledged work orders still waiting planning approval.',
      actionLabel: 'Open Planning',
      path: '/planning',
      category: 'Follow-up',
      accent: THEME.warning
    },
    {
      key: 'maintenanceMroInProgress',
      title: 'Maintenance In Progress',
      value: counts.maintenanceMroInProgress,
      icon: <Handyman fontSize="small" />,
      description: 'Work orders currently in MRO execution (coordination visibility).',
      actionLabel: 'Open Maintenance',
      path: '/maintenance',
      category: 'Visibility',
      accent: THEME.success
    },
    {
      key: 'poAmApprovals',
      title: 'Purchase Order AM Approvals',
      value: counts.poAmApprovals,
      icon: <MonetizationOn fontSize="small" />,
      description: 'Purchase orders in PENDING_AM_APPROVAL state.',
      actionLabel: 'Open Procurement',
      path: '/procurement',
      category: 'Decision',
      accent: THEME.warning
    },
    {
      key: 'planningIntentAmApprovals',
      title: 'Planning Intent AM Approvals',
      value: counts.planningIntentAmApprovals,
      icon: <Inventory2 fontSize="small" />,
      description: 'Independent planning intents (PLANNING_NON_WO) in PENDING status awaiting AM approval.',
      actionLabel: 'Open Planning',
      path: '/planning',
      category: 'Decision',
      accent: '#8b6914'
    },
    {
      key: 'loanAmApprovals',
      title: 'Loan-Out AM Approvals',
      value: counts.loanAmApprovals,
      icon: <Inventory2 fontSize="small" />,
      description: 'Loan-out issuance requests waiting AM action.',
      actionLabel: 'Open Store',
      path: '/store',
      category: 'Decision',
      accent: '#6b5b3e'
    },
    {
      key: 'receivingPendingInspectionStore',
      title: 'Store Receiving — Pending Inspection',
      value: counts.receivingPendingInspectionStore,
      icon: <FactCheck fontSize="small" />,
      description: 'Receiving submissions that are not loan-in (typically PO-backed), awaiting inspector or AM approval.',
      actionLabel: 'Open Receiving',
      path: '/store/receiving',
      navigateState: { receivingInventoryDomain: 'STORE', receivingWorkflowFilter: 'PENDING' },
      category: 'Decision',
      accent: THEME.info
    },
    {
      key: 'receivingPendingInspectionLoan',
      title: 'Loan-In Receiving — Pending Inspection',
      value: counts.receivingPendingInspectionLoan,
      icon: <MoveToInbox fontSize="small" />,
      description: 'Borrowed parts loaned in via Store Receiving (loan reference, no PO), awaiting inspector or AM approval.',
      actionLabel: 'Open Receiving',
      path: '/store/receiving',
      navigateState: { receivingInventoryDomain: 'STORE', receivingWorkflowFilter: 'PENDING' },
      category: 'Decision',
      accent: '#2a6f97'
    },
    {
      key: 'changeRequestAmApprovals',
      title: 'Change Request AM Approvals',
      value: counts.changeRequestAmApprovals,
      icon: <FeedbackIcon fontSize="small" />,
      description: 'Application change request forms awaiting Accountable Manager or Super Admin approval.',
      actionLabel: 'Open Feedback',
      path: '/feedback',
      navigateState: {
        feedbackFilters: {
          type: 'Change Request Form',
          amApprovalStatus: 'PENDING'
        }
      },
      category: 'Decision',
      accent: '#7a5c45'
    }
  ];

  const shortcuts = [
    { label: 'Charter Approvals', path: '/sales' },
    { label: 'Maintenance Workflow', path: '/maintenance' },
    { label: 'Planning Board', path: '/planning' },
    { label: 'Store Approvals', path: '/store' },
    { label: 'PO Pipeline', path: '/procurement' },
    { label: 'MIS Overview', path: '/mis-dashboard' }
  ];

  const chartEmpty = !loading && compositionData.length === 0;

  return (
    <Box
      sx={{
        mx: { xs: -1, sm: -2 },
        mb: -2,
        px: { xs: 1.5, sm: 2.5 },
        pb: 3,
        pt: 0.5,
        minHeight: '100%',
        background: `
          radial-gradient(ellipse 80% 50% at 100% -10%, ${alpha(THEME.gold, 0.12)} 0%, transparent 55%),
          radial-gradient(ellipse 60% 40% at 0% 0%, ${alpha(THEME.steel, 0.1)} 0%, transparent 50%),
          linear-gradient(180deg, ${THEME.paper} 0%, #eef2f7 100%)
        `
      }}
    >
      {/* Hero header */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: 3,
          overflow: 'hidden',
          border: `1px solid ${THEME.border}`,
          background: `linear-gradient(125deg, ${THEME.ink} 0%, ${THEME.navy} 48%, ${THEME.steel} 100%)`,
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: `
              linear-gradient(90deg, transparent 60%, ${alpha(THEME.gold, 0.15)} 100%),
              repeating-linear-gradient(
                -12deg,
                transparent,
                transparent 18px,
                ${alpha('#ffffff', 0.015)} 18px,
                ${alpha('#ffffff', 0.015)} 19px
              )
            `,
            pointerEvents: 'none'
          }
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
          sx={{ p: { xs: 2.5, md: 3 }, position: 'relative', zIndex: 1 }}
        >
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 1.25,
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: alpha(THEME.gold, 0.2),
                  color: THEME.goldSoft,
                  border: `1px solid ${alpha(THEME.goldSoft, 0.35)}`
                }}
              >
                <Assessment fontSize="small" />
              </Box>
              <Chip
                size="small"
                label="Accountable Manager"
                sx={{
                  bgcolor: alpha('#ffffff', 0.1),
                  color: alpha('#ffffff', 0.9),
                  border: `1px solid ${alpha('#ffffff', 0.18)}`,
                  fontWeight: 600,
                  letterSpacing: 0.4
                }}
              />
            </Stack>
            <Typography
              variant="h4"
              sx={{
                color: '#fff',
                fontWeight: 700,
                letterSpacing: '-0.02em',
                mb: 0.75
              }}
            >
              Executive Coordination Desk
            </Typography>
            <Typography variant="body2" sx={{ color: alpha('#ffffff', 0.72), maxWidth: 560 }}>
              Approve critical decisions and monitor cross-functional workload across charter, store,
              planning, and maintenance.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
            <Box sx={{ textAlign: { xs: 'left', md: 'right' }, mr: { md: 1 } }}>
              <Typography variant="caption" sx={{ color: alpha('#ffffff', 0.55), display: 'block' }}>
                Last refreshed
              </Typography>
              <Typography variant="body2" sx={{ color: alpha('#ffffff', 0.9), fontWeight: 600 }}>
                {formatUpdatedAt(lastUpdated)}
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Refresh />}
              onClick={fetchData}
              disabled={loading}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                bgcolor: THEME.gold,
                color: THEME.ink,
                boxShadow: 'none',
                px: 2.25,
                '&:hover': { bgcolor: THEME.goldSoft, boxShadow: 'none' },
                '&.Mui-disabled': { bgcolor: alpha(THEME.gold, 0.4), color: alpha(THEME.ink, 0.5) }
              }}
            >
              {loading ? 'Refreshing…' : 'Refresh'}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Summary metrics */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <SummaryMetric
            label="Pending AM decisions"
            value={pendingAmDecisions}
            hint="Items requiring your direct approval"
            tone="gold"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <SummaryMetric
            label="Follow-up workload"
            value={followUpWorkload}
            hint="Cross-team closure and in-progress items"
            tone="navy"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <SummaryMetric
            label="Total open items"
            value={totalOpenItems}
            hint={totalOpenItems === 0 ? 'All queues clear' : 'Combined coordination load'}
            tone={totalOpenItems === 0 ? 'success' : 'warning'}
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Visualizations */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} lg={5}>
          <Paper
            elevation={0}
            sx={{
              height: '100%',
              p: 2.5,
              borderRadius: 2.5,
              border: `1px solid ${THEME.border}`,
              bgcolor: THEME.card
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Gavel sx={{ color: THEME.gold, fontSize: 20 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: THEME.ink }}>
                Workload composition
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{ color: THEME.muted, mb: 2 }}>
              Share of items awaiting your decision versus follow-up coordination.
            </Typography>

            {loading ? (
              <Box sx={{ height: 260, display: 'grid', placeItems: 'center' }}>
                <CircularProgress size={32} sx={{ color: THEME.steel }} />
              </Box>
            ) : chartEmpty ? (
              <Box
                sx={{
                  height: 260,
                  display: 'grid',
                  placeItems: 'center',
                  borderRadius: 2,
                  bgcolor: alpha(THEME.success, 0.06),
                  border: `1px dashed ${alpha(THEME.success, 0.35)}`
                }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" sx={{ color: THEME.success, fontWeight: 700 }}>
                    Queues clear
                  </Typography>
                  <Typography variant="body2" sx={{ color: THEME.muted }}>
                    No open AM or follow-up items right now.
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box sx={{ height: 260, position: 'relative' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={compositionData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="46%"
                      innerRadius={62}
                      outerRadius={92}
                      paddingAngle={3}
                      stroke={THEME.card}
                      strokeWidth={3}
                    >
                      {compositionData.map((entry) => (
                        <Cell key={entry.name} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip content={<ChartTooltipContent />} />
                    <Legend
                      verticalAlign="bottom"
                      height={36}
                      formatter={(value) => (
                        <span style={{ color: THEME.muted, fontSize: 12 }}>{value}</span>
                      )}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <Box
                  sx={{
                    position: 'absolute',
                    top: '42%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    textAlign: 'center',
                    pointerEvents: 'none'
                  }}
                >
                  <Typography variant="caption" sx={{ color: THEME.muted, display: 'block' }}>
                    Total
                  </Typography>
                  <Typography variant="h5" sx={{ color: THEME.ink, fontWeight: 700, lineHeight: 1.1 }}>
                    {totalOpenItems}
                  </Typography>
                </Box>
              </Box>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} lg={7}>
          <Paper
            elevation={0}
            sx={{
              height: '100%',
              p: 2.5,
              borderRadius: 2.5,
              border: `1px solid ${THEME.border}`,
              bgcolor: THEME.card
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <Timeline sx={{ color: THEME.steel, fontSize: 20 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: THEME.ink }}>
                Queue depth by area
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{ color: THEME.muted, mb: 2 }}>
              Active queues only — empty lanes are omitted for clarity.
            </Typography>

            {loading ? (
              <Box sx={{ height: 260, display: 'grid', placeItems: 'center' }}>
                <CircularProgress size={32} sx={{ color: THEME.steel }} />
              </Box>
            ) : queueBarData.length === 0 ? (
              <Box
                sx={{
                  height: 260,
                  display: 'grid',
                  placeItems: 'center',
                  borderRadius: 2,
                  bgcolor: THEME.mist
                }}
              >
                <Typography variant="body2" sx={{ color: THEME.muted }}>
                  No active queues to chart.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ height: Math.max(220, queueBarData.length * 36), minHeight: 260 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={queueBarData}
                    layout="vertical"
                    margin={{ top: 4, right: 16, left: 8, bottom: 4 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={THEME.border} horizontal={false} />
                    <XAxis
                      type="number"
                      allowDecimals={false}
                      tick={{ fill: THEME.muted, fontSize: 11 }}
                      axisLine={{ stroke: THEME.border }}
                      tickLine={false}
                    />
                    <YAxis
                      type="category"
                      dataKey="name"
                      width={118}
                      tick={{ fill: THEME.ink, fontSize: 11, fontWeight: 500 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <RechartsTooltip content={<ChartTooltipContent />} cursor={{ fill: alpha(THEME.steel, 0.06) }} />
                    <Bar dataKey="value" name="Items" radius={[0, 6, 6, 0]} maxBarSize={22} barSize={18}>
                      {queueBarData.map((entry) => (
                        <Cell key={entry.name} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Action cards */}
      <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Box>
          <Typography variant="h6" sx={{ color: THEME.ink, fontWeight: 700 }}>
            Action queues
          </Typography>
          <Typography variant="body2" sx={{ color: THEME.muted }}>
            Open a module or use task shortcuts for focused review.
          </Typography>
        </Box>
      </Stack>

      <Grid container spacing={2}>
        {cards.map((card) => (
          <Grid item xs={12} sm={6} xl={4} key={card.key}>
            <WorkloadCard
              card={card}
              loading={loading}
              onOpen={() => navigateCardPrimary(card)}
              onOpenTasks={(e) => {
                setTaskMenuAnchor(e.currentTarget);
                setTaskMenuKey(card.key);
              }}
            />
          </Grid>
        ))}
      </Grid>

      <Menu
        anchorEl={taskMenuAnchor}
        open={Boolean(taskMenuAnchor) && Boolean(taskMenuKey)}
        onClose={() => {
          setTaskMenuAnchor(null);
          setTaskMenuKey(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{
          sx: {
            mt: 0.5,
            borderRadius: 2,
            border: `1px solid ${THEME.border}`,
            boxShadow: `0 12px 32px ${alpha(THEME.ink, 0.12)}`,
            minWidth: 260
          }
        }}
      >
        {(taskActionsByCard[taskMenuKey] || []).map((item) => (
          <MenuItem
            key={item.label}
            onClick={() => {
              item.action();
              setTaskMenuAnchor(null);
              setTaskMenuKey(null);
            }}
            sx={{ fontSize: '0.875rem', py: 1.1 }}
          >
            {item.label}
          </MenuItem>
        ))}
      </Menu>

      {/* Shortcuts */}
      <Paper
        elevation={0}
        sx={{
          mt: 3,
          p: 2.5,
          borderRadius: 2.5,
          border: `1px solid ${THEME.border}`,
          bgcolor: THEME.card,
          backgroundImage: `linear-gradient(135deg, ${alpha(THEME.navy, 0.03)} 0%, transparent 55%)`
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: THEME.ink, mb: 0.5 }}>
          Quick coordination shortcuts
        </Typography>
        <Typography variant="body2" sx={{ color: THEME.muted, mb: 2 }}>
          Jump directly into the boards you coordinate most often.
        </Typography>
        <Divider sx={{ mb: 2, borderColor: THEME.border }} />
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {shortcuts.map((item) => (
            <Button
              key={item.path + item.label}
              size="small"
              variant="outlined"
              onClick={() => navigate(item.path)}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderColor: THEME.border,
                color: THEME.navy,
                bgcolor: alpha(THEME.navy, 0.02),
                borderRadius: 2,
                px: 1.75,
                '&:hover': {
                  borderColor: THEME.steel,
                  bgcolor: alpha(THEME.steel, 0.08)
                }
              }}
            >
              {item.label}
            </Button>
          ))}
        </Stack>
        <Typography variant="caption" sx={{ color: THEME.muted, display: 'block', mt: 2 }}>
          Pending decisions include charter, PO, planning intents, loan-out, store and loan-in receiving
          inspection, and change request feedback. Follow-up workload tracks cross-team closure items and
          in-progress maintenance.
        </Typography>
      </Paper>
    </Box>
  );
};

export default AccountableManagerDashboard;
